import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn";

import {
    failContract,
    hasOwn,
    isRecord,
    requireArray,
    requireKnownEnum,
    requireNumber,
    requireRecord,
    requireString,
    requireTimezoneAwareDateTime,
} from "./contractValidation";
import { decodeMimeEncodedWords } from "./inboxConversationListViewModel";
import { buildMailClassificationDisplay } from "./mailClassificationDisplay";

dayjs.extend(relativeTime);

const UNKNOWN_SUBJECT = "无主题";
const UNKNOWN_SENDER = "未知发件人";
const UNKNOWN_RECIPIENT = "未标明收件人";

const ENTRY_LANGUAGE = {
    inbox: "收件箱",
    business_object: "业务对象",
    review: "审核",
};

const ENTITY_TYPE_LANGUAGE = {
    flight: "航班",
    flight_operation: "航班运行",
    flight_cargo_load: "货邮装载",
    schedule: "航班计划",
    flight_schedule: "航班计划",
    catering_order: "配餐订单",
    catering_service: "配餐服务",
    fuel_request: "燃油申请",
    permit_request: "许可申请",
    crew_transport: "机组接送",
    crew_transportation: "机组接送",
    invoice: "账单",
    settlement_invoice: "账单",
    ap_invoice: "账单",
    order: "订单",
    supplier: "供应商",
    customer: "客户",
};

const LINK_KIND_LANGUAGE = {
    factual: "事实关联",
    fact: "事实关联",
    handling: "处置关联",
    action: "处置关联",
};

const SOURCE_METHOD_LANGUAGE = {
    rule: "规则识别",
    agent: "AG 判断",
    manual: "人工确认",
    business_callback: "业务回写",
};

const LINK_TYPE_LANGUAGE = {
    mvt_parse: "报文解析",
    cpm_parse: "报文解析",
    ffm_parse: "报文解析",
    fwb_parse: "报文解析",
    agent_judgement: "AG 判断",
    agent_judgment: "AG 判断",
    manual_write: "人工确认",
    source_inherited: "来源继承",
};

const WORK_ITEM_STATUS_LANGUAGE = {
    new: { label: "待处理", tone: "neutral" },
    pending: { label: "待处理", tone: "warning" },
    processing: { label: "处理中", tone: "processing" },
    in_progress: { label: "处理中", tone: "processing" },
    dispatched: { label: "处理中", tone: "processing" },
    running: { label: "处理中", tone: "processing" },
    completed: { label: "已完成", tone: "success" },
    done: { label: "已完成", tone: "success" },
    success: { label: "已完成", tone: "success" },
    failed: { label: "处理异常", tone: "danger" },
    error: { label: "处理异常", tone: "danger" },
    dead: { label: "需要人工处理", tone: "danger" },
    cancelled: { label: "已取消", tone: "neutral" },
    canceled: { label: "已取消", tone: "neutral" },
};

const AG_REVIEW_STATUS_LANGUAGE = {
    open: { label: "待审核", tone: "warning" },
    closed: { label: "已关闭", tone: "neutral" },
};

const BODY_SEGMENT_KIND_LANGUAGE = {
    new_body: "新增正文",
    quoted_history: "历史引用",
    signature: "签名",
    disclaimer: "免责声明",
    unknown: "未分类正文",
};

const BODY_SEGMENT_KINDS = new Set(Object.keys(BODY_SEGMENT_KIND_LANGUAGE));
const BODY_CONTENT_FORMATS = new Set(["markdown", "html", "text"]);

const BODY_BOUNDARY_SOURCE_LANGUAGE = {
    outlook_header_cluster: "Outlook/Exchange 头簇",
    chinese_outlook_header_cluster: "中文回复头簇",
    gmail_on_wrote: "Gmail wrote 分界",
    inline_quote: "行内引用",
    blockquote: "HTML 引用块",
    html_reply_separator: "HTML 回复分隔符",
};

const BODY_SOURCE_ASSET_LANGUAGE = {
    markdown: "Markdown Asset",
    plain_text: "Plain Text Asset",
    cleaned_html: "Cleaned HTML Asset",
};

const RENDERABLE_BODY_FORMATS = new Set(["html", "plain_text", "text"]);
const SAFE_INLINE_ATTACHMENT_PREVIEW_CONTENT_TYPES = new Set([
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/webp",
    "image/bmp",
    "image/avif",
    "text/plain",
]);

const IMAGE_RESOURCE_STATUSES = new Set(["loadable", "unavailable", "remote_blocked", "upstream_placeholder", "tracking_pixel"]);

const CONFIRMED_STATUSES = new Set(["", "active", "applied", "approved", "accepted", "confirmed", "linked", "manual", "system", "verified"]);
const PENDING_REVIEW_STATUSES = new Set(["open", "pending", "pending_review", "review", "under_review"]);
const PENDING_CONFIRM_STATUSES = new Set(["candidate", "mentioned", "mention", "proposed", "unconfirmed"]);
const REJECTED_STATUSES = new Set(["closed", "dismissed", "excluded", "ignored", "rejected"]);
const CONFLICT_STATUSES = new Set(["conflict", "conflicted"]);
const UNKNOWN_STATUSES = new Set(["", "unknown"]);

function toArray(value) {
    return Array.isArray(value) ? value : [];
}

function toRecord(value) {
    return isRecord(value) ? value : {};
}

function normalizeWhitespace(value) {
    return String(value).replace(/\s+/g, " ").trim();
}

function normalizeInlineText(value, fallback = "") {
    if (value === null || value === undefined) return fallback;
    const decoded = normalizeWhitespace(decodeMimeEncodedWords(String(value)));
    return decoded || fallback;
}

function normalizeBodyText(value) {
    if (value === null || value === undefined) return "";
    return decodeMimeEncodedWords(String(value)).trim();
}

function normalizeBodyContent(value) {
    if (value === null || value === undefined) return "";
    return String(value).trim();
}

export function normalizeContentType(value) {
    return normalizeInlineText(value).split(";", 1)[0].trim().toLowerCase();
}

export function isSafeInlineAttachmentPreviewContentType(value) {
    return SAFE_INLINE_ATTACHMENT_PREVIEW_CONTENT_TYPES.has(normalizeContentType(value));
}

function normalizeBodyFormat(value, fallback = "text") {
    const key = normalizeInlineText(value).toLowerCase();
    if (key === "markdown") return "markdown";
    if (key === "html" || key === "cleaned_html") return "html";
    if (key === "text" || key === "plain_text") return "text";
    const fallbackKey = normalizeInlineText(fallback).toLowerCase();
    return BODY_CONTENT_FORMATS.has(fallbackKey) ? fallbackKey : "text";
}

function normalizeRenderableBodyFormat(value, fallback = "text") {
    const key = normalizeInlineText(value).toLowerCase();
    if (key === "html") return "html";
    if (key === "plain_text" || key === "text") return "text";
    const fallbackKey = normalizeInlineText(fallback).toLowerCase();
    if (fallbackKey === "plain_text") return "text";
    return RENDERABLE_BODY_FORMATS.has(fallbackKey) ? fallbackKey : "text";
}

function normalizeBodySegmentKind(value) {
    const key = normalizeInlineText(value, "unknown").toLowerCase();
    return BODY_SEGMENT_KINDS.has(key) ? key : "unknown";
}

function toNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
}

function buildTime(value, path) {
    const parsed = requireTimezoneAwareDateTime(value, path);
    return {
        value: parsed,
        timestamp: parsed.valueOf(),
        relativeText: parsed.locale("zh-cn").fromNow(),
        absoluteText: parsed.format("YYYY-MM-DD HH:mm"),
    };
}

function formatShortId(value) {
    const text = normalizeInlineText(value);
    if (!text) return "";
    return text.length > 12 ? `${text.slice(0, 8)}…${text.slice(-4)}` : text;
}

function formatBytes(value) {
    const bytes = toNumber(value, -1);
    if (bytes < 0) return "大小未知";
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let size = bytes / 1024;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }
    return `${size >= 10 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
}

function buildAddress(address, displayName) {
    const normalizedAddress = normalizeInlineText(address);
    const normalizedName = normalizeInlineText(displayName);
    const label = normalizedName && normalizedAddress ? `${normalizedName} <${normalizedAddress}>` : normalizedName || normalizedAddress;

    return {
        address: normalizedAddress,
        displayName: normalizedName,
        label: label || UNKNOWN_SENDER,
    };
}

function buildAddressFromRecord(value) {
    if (!isRecord(value)) return null;
    const record = toRecord(value);
    const address = normalizeInlineText(record.address || record.email || record.email_address);
    const displayName = normalizeInlineText(record.display_name || record.displayName || record.name);
    if (!address && !displayName) return null;
    return buildAddress(address, displayName);
}

function buildAddressList(values) {
    return toArray(values).map(buildAddressFromRecord).filter(Boolean);
}

function formatAddressList(values, fallback = UNKNOWN_RECIPIENT) {
    const labels = values.map((item) => item.label).filter(Boolean);
    return labels.length > 0 ? labels.join("、") : fallback;
}

function buildSender(message = {}) {
    return buildAddress(message.from_address, message.from_display);
}

function getMessageReceiptIds(message = {}, path = "message") {
    return requireArray(message.visible_receipts, `${path}.visible_receipts`)
        .map((receipt, index) => requireString(requireRecord(receipt, `${path}.visible_receipts[${index}]`).id, `${path}.visible_receipts[${index}].id`));
}

function getPrimaryReceipt(message = {}, focusedReceiptId = "") {
    const receipts = toArray(message.visible_receipts);
    if (focusedReceiptId) {
        return receipts.find((receipt) => normalizeInlineText(receipt.id) === focusedReceiptId) || null;
    }
    return receipts[0] || null;
}

function getMessageTimeValue(message = {}) {
    const canonicalMessage = message.canonical_message || {};
    const primaryReceipt = getPrimaryReceipt(message);
    return message.effective_at || canonicalMessage.date || primaryReceipt?.received_at;
}

function normalizeAttachment(item = {}, receiptId = "") {
    const attachmentId = normalizeInlineText(item.id || item.attachment_id);
    const filename = normalizeInlineText(item.filename || item.file_name, attachmentId ? `附件 ${formatShortId(attachmentId)}` : "未命名附件");
    const contentType = normalizeInlineText(item.content_type || item.mime_type, "类型未知");
    const normalizedReceiptId = normalizeInlineText(item.receipt_id || item.mailbox_receipt_id || receiptId);

    return {
        id: attachmentId,
        receiptId: normalizedReceiptId,
        filename,
        contentType,
        sizeBytes: item.size_bytes ?? item.sizeBytes ?? null,
        sizeText: formatBytes(item.size_bytes ?? item.sizeBytes),
        disposition: normalizeInlineText(item.disposition),
        isBodyResource: Boolean(item.is_body_resource || item.isBodyResource),
        canPreview: Boolean(item.can_preview) && isSafeInlineAttachmentPreviewContentType(contentType),
        sourceLabel: item.source === "raw_mime_replay" ? "从原文读取" : "已物化对象",
    };
}

function normalizeThreadMessage(message = {}, index, options = {}) {
    const messagePath = options.path || `threadContext.messages[${index}]`;
    const canonicalMessage = requireRecord(message.canonical_message, `${messagePath}.canonical_message`);
    const receiptIds = getMessageReceiptIds(message, messagePath);
    const primaryReceipt = getPrimaryReceipt(message, options.focusedReceiptId);
    const primaryReceiptId = normalizeInlineText(primaryReceipt?.id || receiptIds[0]);
    const attachments = toArray(message.attachment_entry?.items).map((item) => normalizeAttachment(item, primaryReceiptId));
    const toAddresses = buildAddressList(canonicalMessage.to_addresses);
    const ccAddresses = buildAddressList(canonicalMessage.cc_addresses);
    const bccAddresses = buildAddressList(canonicalMessage.bcc_addresses);
    const time = buildTime(message.effective_at || canonicalMessage.date || primaryReceipt?.received_at, `${messagePath}.effective_at`);
    const canonicalMessageId = normalizeInlineText(canonicalMessage.id);
    const focusedReceiptId = normalizeInlineText(options.focusedReceiptId);
    const focusedCanonicalMessageId = normalizeInlineText(options.focusedCanonicalMessageId);

    return {
        key: canonicalMessageId || primaryReceiptId || String(index),
        canonicalMessageId,
        receiptId: primaryReceiptId,
        receiptIds,
        subject: normalizeInlineText(canonicalMessage.subject, options.fallbackSubject || UNKNOWN_SUBJECT),
        sender: buildSender(canonicalMessage),
        toAddresses,
        ccAddresses,
        bccAddresses,
        recipientsText: formatAddressList(toAddresses),
        time,
        summary: normalizeInlineText(message.summary),
        attachmentCount: toNumber(message.attachment_entry?.count, attachments.length),
        attachments,
        rawMessageAvailable: Boolean(canonicalMessage.raw_message_available || message.body_entry?.raw_access_path),
        isFocused: Boolean(
            (focusedReceiptId && receiptIds.includes(focusedReceiptId))
                || (focusedCanonicalMessageId && canonicalMessageId === focusedCanonicalMessageId),
        ),
    };
}

function buildReceiptFallbackMessage(receiptContext = {}, focusedReceiptId = "") {
    const receipt = requireRecord(receiptContext.receipt, "receiptContext.receipt");
    const canonicalMessage = requireRecord(receiptContext.canonical_message, "receiptContext.canonical_message");
    const receiptId = normalizeInlineText(receipt.id || focusedReceiptId);
    if (!receiptId && !canonicalMessage.id) return null;

    return normalizeThreadMessage(
        {
            canonical_message: canonicalMessage,
            effective_at: canonicalMessage.date || receipt.received_at,
            summary: receiptContext.body?.summary,
            attachment_entry: {
                count: toArray(receiptContext.attachments).length,
                items: toArray(receiptContext.attachments),
            },
            visible_receipts: [
                {
                    id: receiptId,
                    received_at: receipt.received_at,
                },
            ],
        },
        0,
        {
            focusedReceiptId: receiptId,
            focusedCanonicalMessageId: normalizeInlineText(canonicalMessage.id),
        },
    );
}

function buildThreadMessages(threadContext = null, receiptContext = {}, focusedReceiptId = "") {
    const focusedCanonicalMessageId = normalizeInlineText(receiptContext?.canonical_message?.id);
    const fallbackSubject = normalizeInlineText(threadContext?.thread?.subject || receiptContext?.canonical_message?.subject, UNKNOWN_SUBJECT);
    const rawMessages = isRecord(threadContext) ? requireArray(threadContext.messages, "threadContext.messages") : [];
    const messages = rawMessages
        .map((message, index) => normalizeThreadMessage(message, index, {
            focusedReceiptId,
            focusedCanonicalMessageId,
            fallbackSubject,
            path: `threadContext.messages[${index}]`,
        }))
        .sort((left, right) => left.time.timestamp - right.time.timestamp);

    if (isRecord(threadContext)) {
        if (messages.length === 0) {
            failContract("当前 Thread 不可用", "threadContext.messages 为空，不能自动展示其他邮件");
        }
        return messages;
    }

    const fallbackMessage = buildReceiptFallbackMessage(receiptContext, focusedReceiptId);
    return fallbackMessage ? [fallbackMessage] : [];
}

function findInitialReceiptIdFromMessages(messages = []) {
    const normalizedMessages = requireArray(messages, "threadContext.messages")
        .map((message, index) => ({ message, time: buildTime(getMessageTimeValue(message), `threadContext.messages[${index}].effective_at`).timestamp }))
        .sort((left, right) => left.time - right.time);
    const latestMessage = normalizedMessages[normalizedMessages.length - 1]?.message;
    const latestReceipt = getPrimaryReceipt(latestMessage || {});
    return normalizeInlineText(latestReceipt?.id || getMessageReceiptIds(latestMessage || {}, "threadContext.messages[latest]")[0]);
}

export function getMailReadingSurfaceInitialReceiptId(threadContext = {}) {
    const context = requireRecord(threadContext, "threadContext");
    return findInitialReceiptIdFromMessages(context.messages);
}

function formatConfidence(value) {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return "置信度待确认";
    return `${Math.round(Math.max(0, Math.min(number, 1)) * 100)}%`;
}

function buildBodySegment(rawSegment = {}, index, fallbackFormat, segmentation = {}) {
    const segment = toRecord(rawSegment);
    const kind = normalizeBodySegmentKind(segment.kind);
    const content = normalizeBodyText(segment.content);
    if (!content) return null;
    const boundarySource = normalizeInlineText(segment.boundary_source || segment.boundarySource || segmentation.boundary_source);
    const sourceAssetType = normalizeInlineText(segment.provenance?.source_asset_type || segmentation.source_asset?.asset_type);
    const confidence = toNumber(segment.confidence ?? segmentation.confidence, 0);
    const ruleName = normalizeInlineText(segment.provenance?.rule_name);

    return {
        key: normalizeInlineText(segment.id, `body-segment-${index}`),
        kind,
        label: BODY_SEGMENT_KIND_LANGUAGE[kind] || BODY_SEGMENT_KIND_LANGUAGE.unknown,
        content,
        format: normalizeBodyFormat(segment.content_format || segment.contentFormat || segmentation.content_format, fallbackFormat),
        isQuotedHistory: kind === "quoted_history",
        confidence,
        confidenceText: formatConfidence(confidence),
        boundarySource,
        boundarySourceLabel: BODY_BOUNDARY_SOURCE_LANGUAGE[boundarySource] || "未标明分界来源",
        sourceAssetType,
        sourceAssetLabel: BODY_SOURCE_ASSET_LANGUAGE[sourceAssetType] || sourceAssetType || "正文资产",
        ruleName,
        startOffset: toNumber(segment.start_offset ?? segment.startOffset, 0),
        endOffset: toNumber(segment.end_offset ?? segment.endOffset, 0),
    };
}

function buildFallbackBodySegment(selected) {
    if (!selected.content) return [];
    return [
        {
            key: "body-segment-fallback",
            kind: "new_body",
            label: BODY_SEGMENT_KIND_LANGUAGE.new_body,
            content: selected.content,
            format: normalizeBodyFormat(selected.format),
            isQuotedHistory: false,
            confidence: 0,
            confidenceText: "置信度待确认",
            boundarySource: "",
            boundarySourceLabel: "未识别历史引用",
            sourceAssetType: selected.assetType,
            sourceAssetLabel: BODY_SOURCE_ASSET_LANGUAGE[selected.assetType] || "正文资产",
            ruleName: "frontend_unsegmented_fallback",
            startOffset: 0,
            endOffset: selected.content.length,
        },
    ];
}

function normalizeImageResourceStatus(value) {
    const status = normalizeInlineText(value, "unavailable").toLowerCase();
    if (IMAGE_RESOURCE_STATUSES.has(status)) return status;
    return "unavailable";
}

function defaultImagePlaceholderText(status, source) {
    if (status === "remote_blocked") return "远程图片因隐私保护被阻断";
    if (status === "tracking_pixel") return "";
    if (status === "upstream_placeholder") return "邮件原文中的图片占位";
    if (source === "remote") return "远程图片暂不可用";
    if (source === "cid") return "内嵌图片暂不可回放";
    return "图片暂不可回放";
}

function normalizeImageResourceEntry(value = {}) {
    const entry = toRecord(value);
    const attachmentId = normalizeInlineText(entry.attachment_id || entry.attachmentId);
    const contentUrl = normalizeInlineText(entry.content_url || entry.contentUrl);
    const status = normalizeImageResourceStatus(entry.status);
    const source = normalizeInlineText(entry.source, attachmentId || entry.content_id || entry.contentId ? "cid" : "unknown");
    const imageRef = normalizeInlineText(entry.image_ref || entry.imageRef || entry.id || attachmentId || entry.content_id || entry.contentId || status);
    return {
        key: imageRef,
        imageRef,
        source,
        contentId: normalizeInlineText(entry.content_id || entry.contentId),
        attachmentId,
        contentType: normalizeInlineText(entry.content_type || entry.contentType),
        status,
        reason: normalizeInlineText(entry.reason),
        placeholderText: normalizeInlineText(entry.placeholder_text || entry.placeholderText, defaultImagePlaceholderText(status, source)),
        contentUrl,
        isLoadable: status === "loadable" && Boolean(contentUrl),
    };
}

function normalizeRenderableResourcePolicy(value = {}) {
    const policy = toRecord(value);
    const images = toRecord(policy.images);
    const cidImages = toRecord(policy.cid_images || policy.cidImages);
    const remoteImages = toRecord(policy.remote_images || policy.remoteImages);
    const links = toRecord(policy.links);
    const imageEntries = toArray(images.entries).map(normalizeImageResourceEntry).filter((entry) => entry.key);
    const cidImageEntries = imageEntries.filter((entry) => entry.source === "cid");
    const remoteImageEntries = imageEntries.filter((entry) => entry.source === "remote");
    const unavailableEntries = imageEntries.filter((entry) => entry.status === "unavailable");
    const upstreamPlaceholderEntries = imageEntries.filter((entry) => entry.status === "upstream_placeholder");
    const trackingPixelEntries = imageEntries.filter((entry) => entry.status === "tracking_pixel");
    const remoteBlockedEntries = imageEntries.filter((entry) => entry.status === "remote_blocked");

    return {
        images: {
            totalCount: toNumber(images.total_count ?? images.totalCount, imageEntries.length),
            loadableCount: toNumber(images.loadable_count ?? images.loadableCount, imageEntries.filter((entry) => entry.status === "loadable").length),
            unavailableCount: toNumber(images.unavailable_count ?? images.unavailableCount, unavailableEntries.length),
            remoteBlockedCount: toNumber(images.remote_blocked_count ?? images.remoteBlockedCount, remoteBlockedEntries.length),
            trackingPixelCount: toNumber(images.tracking_pixel_count ?? images.trackingPixelCount, trackingPixelEntries.length),
            upstreamPlaceholderCount: toNumber(images.upstream_placeholder_count ?? images.upstreamPlaceholderCount, upstreamPlaceholderEntries.length),
            entries: imageEntries,
        },
        cidImages: {
            strategy: normalizeInlineText(cidImages.strategy, "none"),
            totalReferences: toNumber(cidImages.total_references ?? cidImages.totalReferences, 0),
            loadableCount: toNumber(cidImages.loadable_count ?? cidImages.loadableCount, cidImageEntries.filter((entry) => entry.status === "loadable").length),
            unavailableCount: toNumber(cidImages.unavailable_count ?? cidImages.unavailableCount, cidImageEntries.filter((entry) => entry.status === "unavailable").length),
            entries: cidImageEntries,
        },
        remoteImages: {
            strategy: normalizeInlineText(remoteImages.strategy, "oss_cached_proxy"),
            blockedCount: toNumber(remoteImages.blocked_count ?? remoteImages.blockedCount, remoteBlockedEntries.length),
            loadableCount: toNumber(remoteImages.loadable_count ?? remoteImages.loadableCount, remoteImageEntries.filter((entry) => entry.status === "loadable").length),
            unavailableCount: toNumber(remoteImages.unavailable_count ?? remoteImages.unavailableCount, remoteImageEntries.filter((entry) => entry.status === "unavailable").length),
            trackingPixelCount: toNumber(remoteImages.tracking_pixel_count ?? remoteImages.trackingPixelCount, remoteImageEntries.filter((entry) => entry.status === "tracking_pixel").length),
            entries: remoteImageEntries,
        },
        links: {
            strategy: normalizeInlineText(links.strategy, "new_window_noopener_noreferrer"),
            rewrittenCount: toNumber(links.rewritten_count ?? links.rewrittenCount, 0),
            removedCount: toNumber(links.removed_count ?? links.removedCount, 0),
        },
    };
}

function normalizeRenderableBody(body = {}) {
    const renderable = toRecord(body.renderable_body || body.renderableBody || body.renderable);
    const content = normalizeBodyContent(renderable.content || renderable.rendered_content || renderable.renderedContent);
    if (!content) return null;

    const sourceAsset = toRecord(renderable.source_asset || renderable.sourceAsset);
    const security = toRecord(renderable.security);
    const degradation = toRecord(renderable.degradation);

    return {
        format: normalizeRenderableBodyFormat(renderable.recommended_format || renderable.recommendedFormat || renderable.display_format || renderable.displayFormat),
        assetType: normalizeInlineText(sourceAsset.asset_type || sourceAsset.assetType),
        content,
        contentType: normalizeInlineText(renderable.content_type || renderable.contentType),
        sourceAsset: {
            assetType: normalizeInlineText(sourceAsset.asset_type || sourceAsset.assetType),
            generator: normalizeInlineText(sourceAsset.generator),
            generatorVersion: normalizeInlineText(sourceAsset.generator_version || sourceAsset.generatorVersion),
        },
        security: {
            sanitizerPolicy: normalizeInlineText(security.sanitizer_policy || security.sanitizerPolicy),
            sanitizerVersion: normalizeInlineText(security.sanitizer_version || security.sanitizerVersion),
            resourceRewriteVersion: normalizeInlineText(security.resource_rewrite_version || security.resourceRewriteVersion),
            iframePolicyVersion: normalizeInlineText(security.iframe_policy_version || security.iframePolicyVersion),
            iframeSandbox: normalizeInlineText(security.iframe_sandbox || security.iframeSandbox, "allow-popups allow-popups-to-escape-sandbox"),
            iframeCsp: normalizeInlineText(security.iframe_csp || security.iframeCsp),
        },
        resourcePolicy: normalizeRenderableResourcePolicy(renderable.resource_policy || renderable.resourcePolicy),
        degradation: {
            degraded: Boolean(degradation.degraded),
            reason: normalizeInlineText(degradation.reason),
            fallbackFormat: normalizeRenderableBodyFormat(degradation.fallback_format || degradation.fallbackFormat, "text"),
            messages: toArray(degradation.messages).map((message) => normalizeInlineText(message)).filter(Boolean),
        },
    };
}

function buildBodySegments(body = {}, selected) {
    const segmentation = toRecord(body.body_segmentation || body.bodySegmentation);
    const rawSegments = toArray(segmentation.segments);
    if (rawSegments.length === 0) {
        return buildFallbackBodySegment(selected);
    }

    const fallbackFormat = normalizeBodyFormat(segmentation.content_format, selected.format);
    const segments = rawSegments
        .map((segment, index) => buildBodySegment(segment, index, fallbackFormat, segmentation))
        .filter(Boolean);

    return segments.length > 0 ? segments : buildFallbackBodySegment(selected);
}

function buildBodyView(receiptContext = {}) {
    const body = receiptContext.body || {};
    const renderable = normalizeRenderableBody(body);
    const candidates = [
        { format: "html", assetType: "cleaned_html", content: normalizeBodyText(body.cleaned_html || body.html || body.body_html) },
        { format: "text", assetType: "plain_text", content: normalizeBodyText(body.plain_text || body.text || body.body_text) },
        { format: "markdown", assetType: "markdown", content: normalizeBodyText(body.markdown || body.body_markdown) },
    ];
    const selected = renderable || candidates.find((candidate) => candidate.content) || { format: "empty", assetType: "", content: "" };
    const segments = buildBodySegments(body, selected);
    const quotedSegments = segments.filter((segment) => segment.isQuotedHistory);
    const boundarySegment = quotedSegments[0] || null;

    return {
        format: selected.format,
        content: selected.content,
        sourceAssetType: selected.assetType,
        sourceAssetLabel: BODY_SOURCE_ASSET_LANGUAGE[selected.assetType] || selected.assetType || "正文资产",
        renderableAvailable: Boolean(renderable),
        contentType: renderable?.contentType || "",
        security: renderable?.security || {},
        resourcePolicy: renderable?.resourcePolicy || normalizeRenderableResourcePolicy(),
        degradation: renderable?.degradation || { degraded: false, reason: "", fallbackFormat: "text", messages: [] },
        segments,
        hasQuotedHistory: quotedSegments.length > 0,
        segmentationStatus: normalizeInlineText(body.body_segmentation?.status || body.bodySegmentation?.status, segments.length > 0 ? "available" : "unavailable"),
        boundarySourceLabel: boundarySegment?.boundarySourceLabel || "未识别历史引用",
        confidenceText: boundarySegment?.confidenceText || "置信度待确认",
        summary: normalizeInlineText(body.summary),
    };
}

function normalizeBusinessStatusKey(status, path) {
    const key = status === null || status === undefined
        ? "unknown"
        : requireString(status, path, { allowEmpty: true }).toLowerCase();
    return key;
}

function mapBusinessStatus(status, path) {
    const key = normalizeBusinessStatusKey(status, path);
    if (UNKNOWN_STATUSES.has(key)) return { label: "状态待核对", tone: "neutral" };
    if (CONFLICT_STATUSES.has(key)) return { label: "关联冲突", tone: "danger" };
    if (PENDING_REVIEW_STATUSES.has(key)) return { label: "待审核", tone: "warning" };
    if (PENDING_CONFIRM_STATUSES.has(key)) return { label: "待确认", tone: "warning" };
    if (REJECTED_STATUSES.has(key)) return { label: "已排除", tone: "neutral" };
    if (CONFIRMED_STATUSES.has(key)) return { label: "已确认", tone: "success" };
    failContract("响应契约错误", `${path} 返回未知业务关联状态 ${key}`);
}

function getEntityTypeLabel(entityType, path) {
    return requireKnownEnum(entityType, ENTITY_TYPE_LANGUAGE, path).value;
}

function getSnapshotValue(snapshot, keys) {
    const record = toRecord(snapshot);
    for (const key of keys) {
        if (typeof record[key] === "object") continue;
        const value = normalizeInlineText(record[key]);
        if (value) return value;
    }
    return "";
}

function getBusinessObjectSnapshot(links = []) {
    return links.map((link) => link.entity_snapshot).find(isRecord) || null;
}

function getBusinessObjectDisplayName(entity = {}, links = []) {
    const snapshots = links.map((link) => link.entity_snapshot).filter(isRecord);
    for (const snapshot of snapshots) {
        const label = getSnapshotValue(snapshot, [
            "display_name",
            "displayName",
            "entity_display_name",
            "label",
            "title",
            "name",
            "flight_number",
            "invoice_number",
            "order_number",
            "reference_no",
        ]);
        if (label) return label;
    }

    return formatShortId(entity.entity_id);
}

function getBusinessObjectRoute(links = []) {
    for (const link of links) {
        const snapshot = toRecord(link.entity_snapshot);
        const route = normalizeInlineText(snapshot.route_path);
        if (route.startsWith("/") && !route.startsWith("//")) {
            return route;
        }
    }
    return "";
}

function getBusinessObjectStatus(links = [], path) {
    if (links.length === 0) {
        failContract("响应契约错误", `${path}.links 为空，不能推断业务对象关联状态`);
    }

    const statusKeys = links.map((link, index) => normalizeBusinessStatusKey(link.review_status, `${path}.links[${index}].review_status`));
    statusKeys.forEach((key, index) => mapBusinessStatus(key, `${path}.links[${index}].review_status`));

    if (statusKeys.some((key) => UNKNOWN_STATUSES.has(key))) {
        return mapBusinessStatus("unknown", `${path}.derived_status`);
    }
    if (statusKeys.some((key) => CONFLICT_STATUSES.has(key))) {
        return mapBusinessStatus("conflict", `${path}.derived_status`);
    }
    if (statusKeys.some((key) => PENDING_REVIEW_STATUSES.has(key))) {
        return mapBusinessStatus("pending_review", `${path}.derived_status`);
    }
    if (statusKeys.some((key) => PENDING_CONFIRM_STATUSES.has(key))) {
        return mapBusinessStatus("candidate", `${path}.derived_status`);
    }
    if (statusKeys.length > 0 && statusKeys.every((key) => REJECTED_STATUSES.has(key))) {
        return mapBusinessStatus("rejected", `${path}.derived_status`);
    }
    return mapBusinessStatus("confirmed", `${path}.derived_status`);
}

function buildBusinessObjects(businessLinks = {}) {
    const collection = requireRecord(businessLinks, "business_links");
    return requireArray(collection.grouped_by_entity_type, "business_links.grouped_by_entity_type").flatMap((group, groupIndex) => {
        const groupPath = `business_links.grouped_by_entity_type[${groupIndex}]`;
        const groupRecord = requireRecord(group, groupPath);
        const entityTypeLabel = getEntityTypeLabel(groupRecord.entity_type, `${groupPath}.entity_type`);
        return requireArray(groupRecord.entities, `${groupPath}.entities`).map((entity, entityIndex) => {
            const entityPath = `${groupPath}.entities[${entityIndex}]`;
            const entityRecord = requireRecord(entity, entityPath);
            const links = requireArray(entityRecord.links, `${entityPath}.links`);
            const displayName = getBusinessObjectDisplayName(entity, links);
            const label = displayName ? `${entityTypeLabel} · ${displayName}` : entityTypeLabel;
            const descriptions = Array.from(new Set(links.map((link) => normalizeInlineText(link.description)).filter(Boolean)));

            return {
                key: `${groupIndex}-${entityIndex}-${requireString(entityRecord.entity_id, `${entityPath}.entity_id`)}`,
                entityType: requireString(groupRecord.entity_type, `${groupPath}.entity_type`),
                entityId: requireString(entityRecord.entity_id, `${entityPath}.entity_id`),
                entityTypeLabel,
                snapshot: getBusinessObjectSnapshot(links),
                links,
                label,
                status: getBusinessObjectStatus(links, entityPath),
                linkCount: requireNumber(entityRecord.link_count, `${entityPath}.link_count`, { integer: true, min: 0 }),
                route: getBusinessObjectRoute(links),
                descriptions,
            };
        });
    });
}

function mapLinkKind(link = {}) {
    const key = normalizeInlineText(link.link_kind || link.linkKind).toLowerCase();
    return LINK_KIND_LANGUAGE[key] || "业务关联";
}

function mapSourceMethod(link = {}) {
    const sourceMethod = normalizeInlineText(link.source_method || link.sourceMethod).toLowerCase();
    if (SOURCE_METHOD_LANGUAGE[sourceMethod]) return SOURCE_METHOD_LANGUAGE[sourceMethod];

    const linkType = normalizeInlineText(link.link_type || link.linkType).toLowerCase();
    return LINK_TYPE_LANGUAGE[linkType] || "";
}

function normalizeBusinessLink(link = {}) {
    const status = mapBusinessStatus(link.review_status, "business_links.link.review_status");
    return {
        ...link,
        id: normalizeInlineText(link.id),
        linkKindLabel: mapLinkKind(link),
        sourceMethodLabel: mapSourceMethod(link),
        status,
        description: normalizeInlineText(link.description),
        createdTime: buildTime(link.created_at, "business_links.link.created_at"),
        updatedTime: buildTime(link.updated_at, "business_links.link.updated_at"),
    };
}

function buildConfirmedLinks(businessLinks = {}) {
    const collection = requireRecord(businessLinks, "business_links");
    return requireArray(collection.grouped_by_entity_type, "business_links.grouped_by_entity_type").flatMap((group, groupIndex) => {
        const groupPath = `business_links.grouped_by_entity_type[${groupIndex}]`;
        const groupRecord = requireRecord(group, groupPath);
        const entityType = requireString(groupRecord.entity_type, `${groupPath}.entity_type`);
        const entityTypeLabel = getEntityTypeLabel(entityType, `${groupPath}.entity_type`);
        return requireArray(groupRecord.entities, `${groupPath}.entities`).map((entity, entityIndex) => {
            const entityPath = `${groupPath}.entities[${entityIndex}]`;
            const entityRecord = requireRecord(entity, entityPath);
            const rawLinks = requireArray(entityRecord.links, `${entityPath}.links`);
            const links = rawLinks.map(normalizeBusinessLink);
            const snapshot = getBusinessObjectSnapshot(rawLinks);
            const displayName = getBusinessObjectDisplayName(entity, rawLinks);
            const entityId = requireString(entityRecord.entity_id, `${entityPath}.entity_id`);
            const relationMethods = Array.from(new Set(links.map((link) => (
                [link.linkKindLabel, link.sourceMethodLabel].filter(Boolean).join(" · ")
            )).filter(Boolean)));

            return {
                key: `${entityType || "entity"}:${entityId || entityIndex}:${groupIndex}`,
                entityType,
                entityTypeLabel,
                entityId,
                displayName,
                snapshot,
                links,
                linkCount: requireNumber(entityRecord.link_count, `${entityPath}.link_count`, { integer: true, min: 0 }),
                status: getBusinessObjectStatus(rawLinks, entityPath),
                route: getBusinessObjectRoute(rawLinks),
                relationMethods,
                descriptions: Array.from(new Set(links.map((link) => link.description).filter(Boolean))),
            };
        }).filter((item) => item.links.length > 0 || item.entityId);
    });
}

function mapAgReviewStatus(item = {}, path) {
    if (item.review_case_redacted) return { label: "受权限保护", tone: "neutral" };
    if (item.review_case_unavailable) return { label: "审核状态不可用", tone: "danger" };
    return requireKnownEnum(item.review_case_lifecycle, AG_REVIEW_STATUS_LANGUAGE, `${path}.review_case_lifecycle`).value;
}

function mapWorkItemStatus(status, path) {
    return requireKnownEnum(status, WORK_ITEM_STATUS_LANGUAGE, path).value;
}

function buildReviewSourceStatuses(item = {}, path) {
    const sources = requireArray(item.source_work_items, `${path}.source_work_items`);
    if (sources.length === 0 && (item.review_case_redacted || item.review_case_unavailable)) {
        return [];
    }
    const statuses = sources.length > 0
        ? sources.map((source, index) => mapWorkItemStatus(requireRecord(source, `${path}.source_work_items[${index}]`).work_item_status, `${path}.source_work_items[${index}].work_item_status`))
        : [mapWorkItemStatus(item.work_item_status, `${path}.work_item_status`)];
    const seen = new Set();
    return statuses.filter((status) => {
        if (!status.label || seen.has(status.label)) return false;
        seen.add(status.label);
        return true;
    });
}

function getReviewSummary(item = {}) {
    if (item.review_case_redacted) return "审核内容受权限保护";
    if (item.review_case_unavailable) return "审核事项暂不可用";
    const sourceSummary = toArray(item.source_work_items)
        .map((source) => normalizeInlineText(source.result_summary))
        .find(Boolean);
    return normalizeInlineText(item.review_case_summary || item.result_summary || sourceSummary, "AG 已形成待审核判断");
}

function buildPendingReviews(agReviewCases = {}) {
    const collection = requireRecord(agReviewCases, "ag_review_cases");
    return requireArray(collection.items, "ag_review_cases.items").map((rawItem, index) => {
        const path = `ag_review_cases.items[${index}]`;
        const item = requireRecord(rawItem, path);
        const reviewCaseId = normalizeInlineText(item.review_case_id);
        const sourceStatuses = buildReviewSourceStatuses(item, path);
        const updatedTime = item.review_case_redacted || item.review_case_unavailable
            ? null
            : buildTime(item.review_case_updated_at || item.review_case_created_at || item.created_at, `${path}.review_case_updated_at`);

        return {
            key: reviewCaseId || `review-${index}`,
            reviewCaseId,
            summary: getReviewSummary(item),
            status: mapAgReviewStatus(item, path),
            sourceStatuses,
            sourceCount: requireArray(item.source_work_items, `${path}.source_work_items`).length || (item.work_item_id ? 1 : 0),
            route: reviewCaseId ? `/review/ag/${encodeURIComponent(reviewCaseId)}` : "",
            updatedTime,
            canOpen: Boolean(reviewCaseId),
        };
    });
}

export function buildBusinessSidebar(contextData = {}) {
    const nestedBusinessContext = isRecord(contextData.business_context) ? contextData.business_context : {};
    const businessLinks = contextData.business_links
        || contextData.businessLinks
        || nestedBusinessContext.business_links;
    const agReviewCases = contextData.ag_review_cases
        || contextData.agReviewCases
        || nestedBusinessContext.ag_review_cases;

    return {
        confirmedLinks: buildConfirmedLinks(businessLinks),
        pendingReviews: buildPendingReviews(agReviewCases),
    };
}

function buildAgReviewSignal(agReviewCases = {}) {
    const collection = requireRecord(agReviewCases, "ag_review_cases");
    const pendingCount = requireNumber(collection.pending_count, "ag_review_cases.pending_count", { integer: true, min: 0 });
    const sourceCount = requireNumber(collection.source_count, "ag_review_cases.source_count", { integer: true, min: 0 });
    const closedCount = requireNumber(collection.closed_count, "ag_review_cases.closed_count", { integer: true, min: 0 });
    const unknownCount = requireNumber(collection.unknown_count, "ag_review_cases.unknown_count", { integer: true, min: 0 });
    if (unknownCount > 0) {
        failContract("响应契约错误", "ag_review_cases.unknown_count 表示存在未知审核生命周期");
    }
    const items = requireArray(collection.items, "ag_review_cases.items").map((rawItem, index) => {
        const path = `ag_review_cases.items[${index}]`;
        const item = requireRecord(rawItem, path);
        const lifecycle = requireKnownEnum(item.review_case_lifecycle, AG_REVIEW_STATUS_LANGUAGE, `${path}.review_case_lifecycle`).key;
        const isPending = lifecycle === "open";
        return {
            key: normalizeInlineText(item.review_case_id, String(index)),
            status: AG_REVIEW_STATUS_LANGUAGE[lifecycle],
            summary: normalizeInlineText(item.review_case_summary || item.result_summary, "AG 已形成审核信号"),
            time: item.review_case_redacted || item.review_case_unavailable
                ? null
                : buildTime(item.review_case_updated_at || item.review_case_created_at || item.created_at, `${path}.review_case_updated_at`),
        };
    });

    if (pendingCount > 0) {
        return {
            visible: true,
            status: { label: "待审核", tone: "warning" },
            summary: `${pendingCount} 项 AG 判断待审核`,
            items,
        };
    }

    if (sourceCount > 0) {
        return {
            visible: true,
            status: { label: closedCount > 0 ? "已有记录" : "待确认", tone: "neutral" },
            summary: `${sourceCount} 项 AG 判断记录`,
            items,
        };
    }

    return {
        visible: false,
        status: { label: "无审核信号", tone: "neutral" },
        summary: "暂无 AG 审核信号",
        items: [],
    };
}

function buildParticipants(threadContext = {}, messages = []) {
    const participants = toArray(threadContext?.thread?.participants).map((participant, index) => {
        const address = buildAddress(participant.address, participant.display_name);
        return {
            key: address.address || String(index),
            label: address.label,
            messageCount: toNumber(participant.message_count),
        };
    });

    if (participants.length > 0) return participants;

    const byAddress = new Map();
    for (const message of messages) {
        [message.sender, ...message.toAddresses, ...message.ccAddresses, ...message.bccAddresses].forEach((address) => {
            if (!address.address && !address.displayName) return;
            const key = (address.address || address.label).toLowerCase();
            const current = byAddress.get(key) || { key, label: address.label, messageCount: 0 };
            current.messageCount += 1;
            byAddress.set(key, current);
        });
    }
    return Array.from(byAddress.values());
}

function selectCurrentMessage(messages, focusedReceiptId, receiptContext) {
    const focusedCanonicalMessageId = normalizeInlineText(receiptContext?.canonical_message?.id);
    if (focusedReceiptId) {
        const matchedByReceipt = messages.find((message) => message.isFocused)
            || messages.find((message) => message.receiptIds.includes(focusedReceiptId));
        if (!matchedByReceipt) {
            failContract("当前选择不可用", `receipt ${focusedReceiptId} 不在当前 Thread 时间线中，不能自动展示其他邮件`);
        }
        return matchedByReceipt;
    }

    if (focusedCanonicalMessageId) {
        const matchedByMessage = messages.find((message) => message.canonicalMessageId === focusedCanonicalMessageId);
        if (!matchedByMessage) {
            failContract("当前选择不可用", `canonical message ${focusedCanonicalMessageId} 不在当前 Thread 时间线中，不能自动展示其他邮件`);
        }
        return matchedByMessage;
    }

    return messages[messages.length - 1] || null;
}

function buildReceiptAttachments(receiptContext = {}, fallbackReceiptId = "") {
    const receiptId = normalizeInlineText(receiptContext.receipt?.id || fallbackReceiptId);
    return toArray(receiptContext.attachments)
        .map((item) => normalizeAttachment(item, receiptId))
        .filter((item) => item.id && item.receiptId && !item.isBodyResource);
}

function buildRawMessage(receiptContext = {}, currentMessage = null, fallbackReceiptId = "") {
    const receiptId = normalizeInlineText(receiptContext.receipt?.id || currentMessage?.receiptId || fallbackReceiptId);
    const available = Boolean(receiptContext.canonical_message?.raw_message_available || currentMessage?.rawMessageAvailable);
    return {
        receiptId,
        available: Boolean(receiptId && available),
    };
}

function chooseBusinessContext(threadContext = {}, receiptContext = {}) {
    if (isRecord(threadContext) && hasOwn(threadContext, "business_links")) {
        return requireRecord(threadContext.business_links, "threadContext.business_links");
    }
    if (isRecord(receiptContext?.business_context) && hasOwn(receiptContext.business_context, "business_links")) {
        return requireRecord(receiptContext.business_context.business_links, "receiptContext.business_context.business_links");
    }
    failContract("响应契约错误", "邮件阅读上下文缺少 business_links，不能显示为空业务关联");
}

function chooseAgReviewCases(threadContext = {}, receiptContext = {}) {
    if (isRecord(threadContext) && hasOwn(threadContext, "ag_review_cases")) {
        return requireRecord(threadContext.ag_review_cases, "threadContext.ag_review_cases");
    }
    if (isRecord(receiptContext?.business_context) && hasOwn(receiptContext.business_context, "ag_review_cases")) {
        return requireRecord(receiptContext.business_context.ag_review_cases, "receiptContext.business_context.ag_review_cases");
    }
    failContract("响应契约错误", "邮件阅读上下文缺少 ag_review_cases，不能显示为空 AG 审核信号");
}

export function buildMailReadingSurfaceViewModel({
    entry = "inbox",
    threadContext = null,
    receiptContext = null,
    threadId = "",
    receiptId = "",
    focusEntity = null,
    reviewCaseId = "",
} = {}) {
    const hasThreadContext = threadContext !== null && threadContext !== undefined;
    const hasReceiptContext = receiptContext !== null && receiptContext !== undefined;
    const normalizedThreadContext = hasThreadContext ? requireRecord(threadContext, "threadContext") : null;
    const normalizedReceiptContext = hasReceiptContext ? requireRecord(receiptContext, "receiptContext") : null;

    if (!threadId && !receiptId && !hasThreadContext && !hasReceiptContext) {
        return {
            entry: {
                key: entry,
                label: ENTRY_LANGUAGE[entry] || ENTRY_LANGUAGE.inbox,
            },
            ids: { threadId: "", receiptId: "" },
            focusEntity,
            reviewCaseId: normalizeInlineText(reviewCaseId),
            header: { subject: "", messageCount: 0, participantCount: 0, participants: [] },
            thread: { messages: [], hasMessages: false },
            currentMessage: null,
            body: null,
            attachments: [],
            rawMessage: { receiptId: "", available: false },
            classification: { active: buildMailClassificationDisplay(null), historyCount: 0 },
            business: { objects: [], agReviewSignal: { visible: false }, sidebar: { confirmedLinks: [], pendingReviews: [] }, hasContext: false },
            actions: { businessObjectLinks: [] },
            empty: true,
        };
    }

    if (threadId && !normalizedThreadContext) {
        failContract("当前 Thread 不可用", `已指定 thread ${threadId}，但后端未返回 Thread 上下文`);
    }
    if (receiptId && !normalizedReceiptContext) {
        failContract("当前 Receipt 不可用", `已指定 receipt ${receiptId}，但后端未返回 Receipt 上下文`);
    }

    if (threadId) {
        const returnedThread = requireRecord(normalizedThreadContext.thread, "threadContext.thread");
        const returnedThreadId = requireString(returnedThread.id, "threadContext.thread.id");
        if (returnedThreadId !== threadId) {
            failContract("当前 Thread 不可用", `请求 thread ${threadId} 与返回 thread ${returnedThreadId} 不一致`);
        }
    }

    let receiptContextReceiptId = normalizeInlineText(normalizedReceiptContext?.receipt?.id);
    if (receiptId) {
        const returnedReceipt = requireRecord(normalizedReceiptContext.receipt, "receiptContext.receipt");
        receiptContextReceiptId = requireString(returnedReceipt.id, "receiptContext.receipt.id");
        if (receiptContextReceiptId !== receiptId) {
            failContract("当前选择不可用", `请求 receipt ${receiptId} 与返回 receipt ${receiptContextReceiptId} 不一致`);
        }
    }

    const focusedReceiptId = normalizeInlineText(receiptId || receiptContextReceiptId || (normalizedThreadContext ? getMailReadingSurfaceInitialReceiptId(normalizedThreadContext) : ""));
    if (focusedReceiptId && !normalizedReceiptContext) {
        failContract("当前 Receipt 不可用", `已定位 receipt ${focusedReceiptId}，但后端未返回 Receipt 上下文`);
    }
    if (!normalizedReceiptContext) {
        failContract("当前 Receipt 不可用", "邮件阅读面缺少 Receipt 上下文，不能展示正文、附件或业务侧栏");
    }
    const messages = buildThreadMessages(normalizedThreadContext, normalizedReceiptContext, focusedReceiptId);
    const currentMessage = selectCurrentMessage(messages, focusedReceiptId, normalizedReceiptContext);
    const participants = buildParticipants(normalizedThreadContext, messages);
    const body = buildBodyView(normalizedReceiptContext);
    const receiptAttachments = buildReceiptAttachments(normalizedReceiptContext, focusedReceiptId);
    const attachments = receiptAttachments.length > 0 ? receiptAttachments : currentMessage?.attachments || [];
    const classificationHistory = toArray(normalizedReceiptContext.processing?.classifications);
    const activeClassification = buildMailClassificationDisplay(normalizedReceiptContext.processing?.active_classification);
    const businessLinks = chooseBusinessContext(normalizedThreadContext, normalizedReceiptContext);
    const agReviewCases = chooseAgReviewCases(normalizedThreadContext, normalizedReceiptContext);
    const businessObjects = buildBusinessObjects(businessLinks);
    const agReviewSignal = buildAgReviewSignal(agReviewCases);
    const businessSidebar = buildBusinessSidebar({ business_links: businessLinks, ag_review_cases: agReviewCases });
    const subject = normalizeInlineText(
        normalizedThreadContext?.thread?.subject
            || normalizedReceiptContext?.canonical_message?.subject
            || currentMessage?.subject,
        UNKNOWN_SUBJECT,
    );
    const messageCount = normalizedThreadContext
        ? requireNumber(normalizedThreadContext.thread?.message_count, "threadContext.thread.message_count", { integer: true, min: 0 })
        : messages.length || (normalizedReceiptContext?.receipt ? 1 : 0);
    const resolvedThreadId = normalizeInlineText(threadId || normalizedThreadContext?.thread?.id || normalizedReceiptContext?.canonical_message?.thread_id);

    return {
        entry: {
            key: entry,
            label: ENTRY_LANGUAGE[entry] || ENTRY_LANGUAGE.inbox,
        },
        ids: {
            threadId: resolvedThreadId,
            receiptId: focusedReceiptId,
        },
        focusEntity,
        reviewCaseId: normalizeInlineText(reviewCaseId),
        header: {
            subject,
            messageCount,
            participantCount: participants.length,
            participants,
        },
        thread: {
            messages,
            hasMessages: messages.length > 0,
        },
        currentMessage,
        body,
        attachments,
        rawMessage: buildRawMessage(normalizedReceiptContext, currentMessage, focusedReceiptId),
        classification: {
            active: activeClassification,
            historyCount: classificationHistory.length,
        },
        business: {
            objects: businessObjects,
            agReviewSignal,
            sidebar: businessSidebar,
            hasContext: businessObjects.length > 0 || agReviewSignal.visible,
        },
        actions: {
            businessObjectLinks: businessObjects.filter((item) => item.route),
        },
        empty: !hasThreadContext && !hasReceiptContext,
    };
}
